import { SettingItem } from '@/components/SettingItem/SettingItem';
import { SuspenseLoading } from '@/components/SuspenseErrorBoundary/fallback';
import { useTranslation } from '@/i18n';
import { ButtonV2, Heading } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, Suspense, useCallback, useState } from 'react';
import styles from './ReconciliationServiceSetting.module.scss';
import { Container } from './components/Container/Container';
import { StopIntegrationModal } from './components/StopIntegrationModal/StopIntegrationModal';
import { useReconciliationServiceSetting } from './hooks/useReconciliationServiceSetting';

const cx = classnames.bind(styles);

const InnerReconciliationServiceSetting: FC = memo(() => {
  const { t } = useTranslation();
  const { isEnabled, isPending, startIntegration, stopIntegration } =
    useReconciliationServiceSetting();
  const [isStopIntegrationModalOpen, setIsStopIntegrationModalOpen] =
    useState(false);

  const handleStop = useCallback(() => {
    setIsStopIntegrationModalOpen(true);
  }, []);

  const handleCancelStop = useCallback(() => {
    setIsStopIntegrationModalOpen(false);
  }, []);

  const handleConfirmStop = useCallback(async () => {
    const isStopped = await stopIntegration();
    if (isStopped) {
      setIsStopIntegrationModalOpen(false);
    }
  }, [stopIntegration]);

  return (
    <Container>
      <div className={cx(styles['list-header-container'])}>
        <Heading as='h2' className={cx(styles['heading'])}>
          {t('reconciliation_service_setting_title')}
        </Heading>
        <div className={cx(styles['description-container'])}>
          <div className={cx(styles['description'])}>
            {t('reconciliation_service_setting_description')}
          </div>
          {isEnabled ? (
            <ButtonV2
              color='danger'
              isSecondary
              type='button'
              onClick={handleStop}
            >
              {t('reconciliation_service_setting_stop_button')}
            </ButtonV2>
          ) : (
            <ButtonV2
              color='primary'
              type='button'
              onClick={startIntegration}
              isLoading={isPending}
              buttonContainerClassName={cx(styles['stop-button'])}
            >
              {t('reconciliation_service_setting_start_button')}
            </ButtonV2>
          )}
        </div>
      </div>
      <div className={cx(styles['setting-items'])}>
        <SettingItem
          label={t('reconciliation_service_setting_status_label')}
          value={t(
            isEnabled
              ? 'reconciliation_service_setting_status_linked'
              : 'reconciliation_service_setting_status_not_linked'
          )}
        />
      </div>
      <StopIntegrationModal
        open={isStopIntegrationModalOpen}
        isLoading={isPending}
        onCancel={handleCancelStop}
        onConfirm={handleConfirmStop}
      />
    </Container>
  );
});
InnerReconciliationServiceSetting.displayName =
  'InnerReconciliationServiceSetting';

export const ReconciliationServiceSetting: FC = memo(() => (
  <Suspense fallback={<SuspenseLoading />}>
    <InnerReconciliationServiceSetting />
  </Suspense>
));
ReconciliationServiceSetting.displayName = 'ReconciliationServiceSetting';
